import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { BOLT_STAR_ONBOARDING_DIALOG_DISMISSED_KEY } from '@provider-portal/common/LocalStorageKeys';
import { useObservableAndState } from '@provider-portal/common/hooks/useObservableAndState';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { setStorageItem } from '@provider-portal/common/utils/storage';
import { useBoltStarMonthlyUpdate } from '@provider-portal/mp2-common/topPerformer';

import { selectIsAccountOrSelectedProviderOnboarding } from 'rdx/account/selectors';

import { BoltStarMonthlyUpdateDialogContainer } from 'widgets/BoltStarMonthlyUpdateDialogContainer/BoltStarMonthlyUpdateDialogContainer';
import { BoltStarOnboardingDialogContainer } from 'widgets/BoltStarOnboardingDialogContainer/BoltStarOnboardingDialogContainer';

/**
 * Picks at most one Bolt Star dialog to show. A monthly update outranks the onboarding intro and
 * marks it as seen, so a merchant never gets both.
 */
export function BoltStarDialogs() {
  const { accountService } = useCommonAppServices();

  const { providerId } = useObservableAndState(accountService.getSelectedProviderData$());
  const isOnboarding = useSelector(selectIsAccountOrSelectedProviderOnboarding);

  const isProviderSuitable = providerId && !isOnboarding;

  const {
    update,
    isTopPerformerEnabled: isBoltStarEnabled,
    isLoading: isMonthlyReportLoading,
  } = useBoltStarMonthlyUpdate(isProviderSuitable ? providerId : undefined);

  const allowShowingDialogs = isProviderSuitable && !isMonthlyReportLoading && isBoltStarEnabled;

  useEffect(() => {
    if (allowShowingDialogs && update) {
      setStorageItem(BOLT_STAR_ONBOARDING_DIALOG_DISMISSED_KEY, 'true');
    }
  }, [allowShowingDialogs, update]);

  if (!allowShowingDialogs) {
    return null;
  }

  if (update) {
    return <BoltStarMonthlyUpdateDialogContainer update={update} providerId={providerId} />;
  }

  return <BoltStarOnboardingDialogContainer />;
}
